import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type QueryFunction,
  type MutationFunction,
} from '../common';
import {
  getIntegrationTemplateDetailsKey,
  getIntegrationTemplateListKey,
} from './keys';

//Fetches single integration template per UUID
export const useIntegrationTemplate: QueryFunction<
  unknown,
  [integrationTemplateId: string],
  ReturnType<typeof getIntegrationTemplateDetailsKey>
> = (integrationTemplateId, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getIntegrationTemplateDetailsKey(integrationTemplateId),
    queryFn: ({ signal }) =>
      request(
        `/managed_integrations/integration_templates/${integrationTemplateId}`,
        {
          query,
          init: { signal },
        },
      ),
  });
};

//Fetches entire list of available integration templates
export const useIntegrationTemplateList: QueryFunction<
  any[],
  [],
  ReturnType<typeof getIntegrationTemplateListKey>
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getIntegrationTemplateListKey(),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/integration_templates`, {
        query,
        init: { signal },
      }),
  });
};

//This create an integration template, overwrites QueryData for that individual template,
//and invalidates all related integration template list queries
export const useCreateIntegrationTemplate: MutationFunction<unknown, any> = ({
  query,
  requestOptions,
  queryOptions,
} = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: (body) => {
      return request(`/managed_integrations/`, {
        query,
        init: { method: 'POST', body },
      });
    },
    ...queryOptions,
    mutationKey: ['createIntegrationTemplate'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getIntegrationTemplateDetailsKey(data?.uuid);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({
        queryKey: getIntegrationTemplateListKey(),
      });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

//This edits an integration template, overwrites QueryData for that individual template,
//and invalidates all related integration template list queries
export const useEditIntegrationTemplate: MutationFunction<
  unknown,
  { uuid: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: (requestData) => {
      return request(`/managed_integrations/${requestData.uuid}`, {
        query,
        ...requestOptions,
        init: { method: 'PATCH', body: requestData },
      });
    },
    ...queryOptions,
    mutationKey: ['editIntegrationTemplate'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getIntegrationTemplateDetailsKey(requestData.uuid);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({
        queryKey: getIntegrationTemplateListKey(),
      });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

export default {
  useIntegrationTemplate,
  useIntegrationTemplateList,
  useCreateIntegrationTemplate,
  useEditIntegrationTemplate,
};
